/**
 * Один способ ходить в API — и одно место, где живут ошибки.
 *
 * Токен CSRF читается из cookie и уходит заголовком при каждом изменяющем
 * запросе: cookie браузер отправит и с чужой страницы, а заголовок — нет.
 * Выбранное рабочее пространство передаётся заголовком, а не путём: адрес
 * страницы не должен меняться от того, за какой канал платит клиент.
 */

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
  /** 402 — не поломка, а закрытый платный раздел. Экран для него отдельный. */
  get isPaywall() {
    return this.status === 402;
  }
  get isUnauthorized() {
    return this.status === 401;
  }
}

function cookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

let workspaceId: number | null = null;
export function setWorkspace(id: number | null) {
  workspaceId = id;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET") {
    const token = cookie("tca_csrf");
    if (token) headers["X-CSRF-Token"] = token;
  }
  if (workspaceId !== null) headers["X-Workspace"] = String(workspaceId);

  const response = await fetch(`/api/v1${path}`, {
    method,
    headers,
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { detail: text };
    }
  }

  if (!response.ok) {
    const detail =
      (payload as { detail?: unknown })?.detail ?? `Ошибка ${response.status}`;
    throw new ApiError(
      response.status,
      typeof detail === "string" ? detail : JSON.stringify(detail),
    );
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body ?? {}),
  del: <T>(path: string) => request<T>("DELETE", path),
};

// ── Формы данных, которые отдаёт API ────────────────────────────────────────

export interface Workspace {
  id: number;
  slug: string;
  name: string;
  plan: "free" | "pro";
  plan_expires_at: string | null;
  channel_limit: number;
  role: string;
  role_title: string;
}

export interface User {
  id: number;
  email: string;
  full_name: string;
  is_superuser: boolean;
}

export interface Me {
  user: User;
  workspace: Workspace | null;
  workspaces: Workspace[];
  permissions: string[];
}

export interface Channel {
  id: number;
  slug: string;
  title: string;
  platform: string;
  chat_id: number | null;
  username: string | null;
  timezone: string;
  is_active: boolean;
  source_kind: string;
  tokel_channel_id: number | null;
  subscribers: number | null;
  subscribers_at: string | null;
  has_admin_rights: boolean;
  maturity_hours: number;
  maturity_detected_hours: number | null;
  maturity_is_manual: boolean;
  track: { topics: boolean; ads: boolean; competitors: boolean; experiments: boolean };
  backfill_status: string;
  backfill_posts: number;
  backfill_error: string | null;
  last_ingest_at: string | null;
  last_compute_at: string | null;
  connected_at: string | null;
}

export type Severity = "alert" | "warn" | "info";
export type Confidence = "high" | "medium" | "low";

export interface HealthSignal {
  key: string;
  area: string;
  area_title: string;
  severity: Severity;
  what: string;
  check: string;
  confidence: Confidence;
  confidence_reason: string;
  facts: Record<string, unknown>;
  evidence: Record<string, unknown>;
}

export interface HealthArea {
  key: string;
  title: string;
  status: "ok" | "attention" | "problem" | "unknown";
  summary: string;
  value: number | null;
  unit: string;
  delta_pct: number | null;
}

export interface QualityCheck {
  key: string;
  title: string;
  status: "ok" | "warn" | "fail";
  value: number | null;
  expected: string | null;
  message: string;
  at?: string;
  date?: string;
}

export interface SparkPoint {
  date: string;
  views_median: number | null;
  views_24h_median: number | null;
  subscribers: number | null;
  subscribers_delta: number | null;
  reaction_rate: number | null;
  posts_total: number;
  posts_ads: number;
  data_quality: string;
}

export interface Health {
  status: "ok" | "attention" | "problem" | "unknown";
  headline: string;
  as_of: string;
  areas: HealthArea[];
  signals: HealthSignal[];
  previous_status: string | null;
  computed_at: string;
  quality: QualityCheck[];
  jobs: { job: string; status: string; started_at: string; duration_ms: number | null; error: string | null }[];
  sparkline: SparkPoint[];
}

export interface Insight {
  id: number;
  area: string;
  kind: string;
  title: string;
  statement_type: "fact" | "observation" | "hypothesis";
  confidence: Confidence;
  score: number;
  sample_size: number;
  body_fact: string;
  body_context: string | null;
  body_limitations: string | null;
  action_text: string;
  status: string;
  narrated_by: string | null;
  period: { from: string; to: string };
  has_experiment: boolean;
  facts?: Record<string, unknown>;
  evidence?: Record<string, unknown>;
}

export interface ContentFinding {
  dimension: string;
  level: string;
  level_title: string;
  outcome: string;
  outcome_title: string;
  n_level: number;
  n_rest: number;
  median_level: number | null;
  median_rest: number | null;
  delta_pct: number | null;
  effect: number | null;
  p_value: number | null;
  confidence: Confidence;
  strata_used: number;
  kind: "effect" | "association";
  note: string;
}

export interface Attribution {
  method: string;
  method_title: string;
  subscribers: number | null;
  ci_low: number | null;
  ci_high: number | null;
  confidence: string;
  reasons: string[];
  baseline_daily: number | null;
  baseline_sample: number | null;
  details: Record<string, unknown>;
}

export interface Outcome {
  horizon_days: number;
  attributed: number | null;
  retained: number | null;
  churn_rate: number | null;
  cpa: number | null;
  cpa_retained: number | null;
  cpm: number | null;
  engagement_index: number | null;
  engagement_basis: string | null;
  method: string;
  confidence: string;
  details: Record<string, unknown>;
}

export interface Purchase {
  id: number;
  placement_title: string;
  placement_platform: string;
  placement_username: string | null;
  placed_at: string;
  removed_at: string | null;
  cost_amount: number | null;
  currency: string;
  ad_format: string;
  promised_views: number | null;
  actual_views: number | null;
  declared_subscribers: number | null;
  notes: string | null;
  links: { id: number; link: string; name: string; join_count: number | null }[];
  attribution: Attribution[];
  outcomes: Outcome[];
  recommendation: {
    benchmark_cpa_retained: number;
    sample: number;
    retained_7d: number;
    max_cost: number;
    currency: string;
  } | null;
}

export interface Competitor {
  id: number;
  identifier: string;
  platform: string;
  title: string | null;
  chat_id: number | null;
  subscribers: number | null;
  posts_collected: number;
  status: string;
  last_error: string | null;
  last_sync_at: string | null;
  is_candidate: boolean;
  discovered_by: string;
  discovery_score: number | null;
  discovery_evidence: Record<string, string[]>;
}

export interface Experiment {
  id: number;
  name: string;
  hypothesis: string;
  variable: string;
  variant_a: string;
  variant_b: string;
  primary_metric: string;
  primary_metric_title: string;
  mode: string;
  status: string;
  min_per_variant: number;
  planned_days: number;
  started_at: string | null;
  ends_at: string | null;
  decision: string | null;
  result: Record<string, unknown> | null;
  assignments: {
    total: number;
    by_state: Record<string, number>;
    published_by_variant: Record<string, number>;
  };
  source_insight_id: number | null;
}

export interface MetricDef {
  key: string;
  title: string;
  unit: string;
  source: string;
  definition: string;
  calculation: string;
  population: string;
  limitations: string;
  platforms: string[];
  higher_is_better: boolean | null;
}
